import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import buildUrl from 'lib/buildUrl';
import { content as ContentPropType } from 'lib/CustomPropTypes';

import LeadBorderTop from 'components/LeadBorderTop';
import Link from 'components/Link';
import TeaseCard from 'components/TeaseCard';
import TypeIcon from 'components/TypeIcon';
import TeasePicture from 'components/TeasePicture';
import Unibrow from 'components/Unibrow';

import { hasFeaturedAuthor } from 'lib/featuredAuthor';
import { imageAltForImage } from 'lib/imageAlt';
import { getVerticalName } from 'lib/verticalType';
import AIMS_FLAVORS from 'lib/aimsFlavors';

import styles from './styles.module.scss';

const mapStateToProps = ({ shared }) => ({
  vertical: shared.vertical,
});

@connect(mapStateToProps)
// TODO, detach this from TeaseCard (props, isNative())
class LeadOneUp extends TeaseCard {
  static propTypes = {
    content: ContentPropType,
    railAdjacent: PropTypes.bool,
    hasSibling: PropTypes.bool,
    pkgClassName: PropTypes.string,
  };

  static defaultProps = {
    railAdjacent: false,
    hasSibling: false,
    pkgClassName: 'pkg leadRectangle',
  };

  hasFeaturedAuthor() {
    const { item, type } = this.props.content;
    return hasFeaturedAuthor(item, type);
  }

  featuredAuthor() {
    if (!this.hasFeaturedAuthor()) {
      return null;
    }

    const { authors } = this.props.content.item;

    // below, intentional space after
    return (
      <span className="featuredAuthor color-primary founders-cond f5 f6-m f7-xl lh-title">
        { authors[0].person.name }
        {' '}
      </span>
    );
  }

  featuredAuthorImage() {
    if (!this.hasFeaturedAuthor()) {
      return null;
    }

    const { authors } = this.props.content.item;
    if (!authors[0].person.primaryImage) {
      return null;
    }

    return (
      <div className={styles.featuredAuthorImage}>
        <TeasePicture
          url={authors[0].person.primaryImage.url.primary} // might need to remove primary
          responsiveFlavors={{ s: AIMS_FLAVORS.FOCAL_380x380 }}
          alt={imageAltForImage(authors[0].person.primaryImage)}
        />
      </div>
    );
  }

  picture() {
    const { vertical, railAdjacent, content } = this.props;
    const { type, computedValues } = content;

    let responsiveFlavors = {
      s: AIMS_FLAVORS.FOCAL_758x379,
      m: AIMS_FLAVORS.FOCAL_580x580,
      l: AIMS_FLAVORS.FOCAL_760x380,
      xl: AIMS_FLAVORS.FOCAL_870x435,
    };

    if (vertical === 'today') {
      if (railAdjacent) {
        responsiveFlavors = {
          s: AIMS_FLAVORS.FOCAL_760x760,
          l: AIMS_FLAVORS.FOCAL_760x760,
        };
      } else {
        responsiveFlavors = {
          s: AIMS_FLAVORS.FOCAL_760x760,
          m: AIMS_FLAVORS.FOCAL_1000x500,
          l: AIMS_FLAVORS.FOCAL_760x380,
          xl: AIMS_FLAVORS.FOCAL_1000x500,
        };
      }
    }

    return (
      <TeasePicture
        responsiveFlavors={responsiveFlavors}
        type={type}
        computedValues={computedValues}
        placeholder
        placeholderColor="gray-40"
      />
    );
  }

  content() {
    const { content, vertical } = this.props;
    const {
      id, type, computedValues, item,
    } = content;
    const unibrow = computedValues ? computedValues.unibrow : content.unibrow;
    const { headline } = computedValues || {};
    const primaryVertical = getVerticalName({ item });

    const url = buildUrl(content);

    const eyebrowToolkitClasses = ['f2', 'lh-none', 'founders-mono', 'fw1', 'ttu'];

    const verticalTitleClasses = {
      think: 'lh-title f5 f6-m f7-xl publico-hed gray-100 fw3',
      notThink: 'publico-hed fw4 f7 f8-m f6-l f7-xl plum lh-title relative',
    };

    return (
      <article
        data-contentid={id}
        data-test="lead-one-up__content"
        className={classNames(styles.content, { 'layout-grid-item': !this.props.railAdjacent })}
      >
        <Link to={url} className={styles.picture}>
          {this.picture()}
        </Link>

        <div className={styles.info}>
          {vertical === 'today' && (
            <React.Fragment>
              <TypeIcon
                type={type}
                url={url}
                className={styles.icon}
              />
              <Unibrow
                className={classNames(
                  {
                    think: primaryVertical === 'think',
                  },
                  styles.eyebrow,
                  eyebrowToolkitClasses,
                  'pt6', 'pb1',
                )}
                unibrow={unibrow}
              />
            </React.Fragment>
          )}

          {vertical !== 'today' && (
            <span className={classNames('icon', 'icon-quote-mark', styles.quote)} />
          )}

          <h2 className={primaryVertical === 'think' ? verticalTitleClasses.think : verticalTitleClasses.notThink}>
            <Link to={url}>
              {this.featuredAuthor()}
              <span>
                {headline}
              </span>
            </Link>
          </h2>

          {vertical !== 'today' && (
            <React.Fragment>
              <Unibrow
                className={classNames(
                  {
                    think: primaryVertical === 'think',
                  },
                  styles.eyebrow,
                  eyebrowToolkitClasses,
                )}
                unibrow={unibrow}
              />
              { this.featuredAuthorImage() }
            </React.Fragment>
          )}
        </div>
      </article>
    );
  }

  render() {
    if (!this.props.content) { return null; }

    const {
      packageId, railAdjacent, hasSibling, pkgClassName,
    } = this.props;
    const { packageIndex } = this.props.content;
    const classes = classNames(
      pkgClassName,
      `pkg-index-${packageIndex + 1}`,
      styles.leadOneUp,
      {
        [styles.sponsored]: this.isNative(),
        [styles.railAdjacent]: railAdjacent,
        mb8: hasSibling,
      },
    );

    return (
      <section data-packageid={packageId} className={classes}>
        <LeadBorderTop types={['collectionLead']} />
        <div
          className={classNames(styles.wrapper, { 'layout-grid-container': !this.props.railAdjacent })}
        >
          { this.content() }
        </div>
      </section>
    );
  }
}

export default LeadOneUp;
